import { Check } from "lucide-react";
import React from "react";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

import { cn } from "./utils";

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    className={cn("ds-radio-group", className)}
    ref={ref}
    {...props}
  />
));

RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

type RadioCardProps = Omit<
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
  "asChild" | "children"
> & {
  cardChecked?: boolean;
  cardProps?: React.HTMLAttributes<HTMLDivElement>;
  children?: React.ReactNode;
  controlProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
};

const RadioCard = React.forwardRef<HTMLDivElement, RadioCardProps>(
  (
    { cardChecked, cardProps, children, className, controlProps, ...props },
    ref
  ) => {
    const {
      className: controlClassName,
      type: controlType = "button",
      ...restControlProps
    } = controlProps || {};
    const { className: cardClassName, ...restCardProps } = cardProps || {};
    const controlContent = (
      <RadioGroupPrimitive.Indicator className="ds-radio-card__indicator">
        <Check aria-hidden="true" size={14} strokeWidth={3} />
      </RadioGroupPrimitive.Indicator>
    );

    if (controlProps) {
      return (
        <div
          {...restCardProps}
          className={cn("ds-radio-card", className, cardClassName)}
          data-state={cardChecked ? "checked" : "unchecked"}
          ref={ref}
        >
          <RadioGroupPrimitive.Item
            className={cn("ds-radio-card__control", controlClassName)}
            type={controlType}
            {...props}
            {...restControlProps}
          >
            {controlContent}
          </RadioGroupPrimitive.Item>
          <div className="ds-radio-card__content">{children}</div>
        </div>
      );
    }

    return (
      <RadioGroupPrimitive.Item asChild {...props}>
        <div className={cn("ds-radio-card", className)} ref={ref}>
          <span aria-hidden="true" className="ds-radio-card__control">
            {controlContent}
          </span>
          <div className="ds-radio-card__content">{children}</div>
        </div>
      </RadioGroupPrimitive.Item>
    );
  }
);

RadioCard.displayName = "RadioCard";

export { RadioCard, RadioGroup };
